import type { IProjectModel } from "@models/project/IProjectModel";
import {
  updateEntityTransformsWithUndoAsync,
  type MediaTilePoseWrite,
} from "@models/project/entityTransformUndo";
import {
  CONTENT_ROOT_SNAPSHOT_NAME,
  K_CONTENT_ROOT_HIERARCHY_ID,
  type EntitySnapshot,
  type EntityTransform,
  type Vec3,
} from "@models/data/entityData";
import { MEDIA_TILE_DEFAULT_SCALE, mediaTilePose } from "@models/data/mediaTileData";
import type { SelectionSetPose } from "@models/data/selectionSetTransform";
import type {
  IMediaTileGeometryHost,
  IPlacementGeometryHost,
  IPlacementModel,
  ISelectionSetTransformHost,
  TransformChannel,
} from "./IPlacementModel";

/**
 * Web port of the first useful `PlacementModel` actions. It delegates all
 * persistence to `IProjectModel` so inspector buttons and future gizmo drags
 * share the same write path.
 *
 * Every write here, entity or card, is ONE undo step (`entityTransformUndo.ts`,
 * `updateMediaTileWithUndoAsync`), as in Unity, where each Placement action sits inside `BeginEditPosition` /
 * `EndEditPosition` or their rotation and scale twins
 * (`PlacementViewModel.OnDropOnTheTable`, `SelectedGameObjectModel.ResetPosition`
 * and its siblings). An action that changes nothing writes nothing.
 */
export class PlacementModel implements IPlacementModel {
  private readonly m_project: IProjectModel;
  private m_geometryHost: IPlacementGeometryHost | null = null;
  private m_mediaTileGeometryHost: IMediaTileGeometryHost | null = null;
  private m_selectionSetHost: ISelectionSetTransformHost | null = null;

  constructor(project: IProjectModel) {
    this.m_project = project;
  }

  attachGeometryHost(host: IPlacementGeometryHost | null): void {
    this.m_geometryHost = host;
  }

  attachMediaTileGeometryHost(host: IMediaTileGeometryHost | null): void {
    this.m_mediaTileGeometryHost = host;
  }

  attachSelectionSetTransformHost(host: ISelectionSetTransformHost | null): void {
    this.m_selectionSetHost = host;
  }

  tryTransformSelectionSet(
    sceneId: string,
    selectionSetId: string,
    memberIds: Iterable<string>,
    pose: SelectionSetPose,
  ): Promise<void> | null {
    return this.m_selectionSetHost?.transformSelectionSet(sceneId, selectionSetId, memberIds, pose) ?? null;
  }

  async setTransformAsync(
    sceneId: string,
    snapshotId: string,
    transform: Partial<EntityTransform>,
  ): Promise<void> {
    await updateEntityTransformsWithUndoAsync(
      this.m_project,
      sceneId,
      { writes: [{ snapshotId, transform }] },
      "Placement",
    );
  }

  async placeOnGroundAsync(sceneId: string, snapshotId: string): Promise<void> {
    const tile = this.m_project.getMediaTile(sceneId, snapshotId);
    if (tile) {
      if (tile.isLocked) return;
      const grounded = this.m_mediaTileGeometryHost?.calculateGroundedMediaTilePosition(sceneId, snapshotId);
      if (!grounded) return;
      // A Position write, which Unity records (`BeginEditPosition` / `EndEditPosition`).
      await this.m_project.updateMediaTileWithUndoAsync(sceneId, snapshotId, { position: grounded }, ["Transform"], {
        label: "Place card on ground",
      });
      return;
    }
    const position = this.m_geometryHost?.calculateGroundedPosition(sceneId, snapshotId);
    if (!position) return;
    await updateEntityTransformsWithUndoAsync(
      this.m_project,
      sceneId,
      { writes: [{ snapshotId, transform: { position } }] },
      "Place on ground",
    );
  }

  async resetPositionAsync(sceneId: string, snapshotId: string): Promise<void> {
    await this.resetChannelsAsync(sceneId, [snapshotId], ["position"], "Reset position");
  }

  async resetRotationAsync(sceneId: string, snapshotId: string): Promise<void> {
    await this.resetChannelsAsync(sceneId, [snapshotId], ["rotation"], "Reset rotation");
  }

  async resetTransformAsync(sceneId: string, snapshotId: string): Promise<void> {
    await this.resetChannelsAsync(sceneId, [snapshotId], ["position", "rotation"], "Reset transform");
  }

  async resetScaleAsync(sceneId: string, snapshotId: string): Promise<void> {
    // Unity `SelectedGameObjectModel.ResetScale` → `GetOriginalScale`: the
    // original imported `PartData` scale, not a hardcoded [1,1,1] (the
    // snapshot's resetTransform carries it, with a [1,1,1] fallback).
    await this.resetChannelsAsync(sceneId, [snapshotId], ["scale"], "Reset scale");
  }

  async resetTransformsAsync(
    sceneId: string,
    snapshotIds: ReadonlyArray<string>,
    channels: ReadonlyArray<TransformChannel>,
  ): Promise<void> {
    await this.resetChannelsAsync(sceneId, snapshotIds, channels, "Reset");
  }

  async alignToCadOriginsAsync(sceneId: string, snapshotId: string): Promise<void> {
    const tile = this.m_project.getMediaTile(sceneId, snapshotId);
    if (tile) {
      // A card has no CAD origin, so Unity's align sends it to the scene's:
      // position zero, rotation identity, scale one, as one undoable step
      // (`SelectedGameObjectModel.cs:1725-1802`, `ProjectModel_Utils.cs:869-885`).
      if (tile.isLocked) return;
      await this.m_project.updateMediaTileWithUndoAsync(
        sceneId,
        snapshotId,
        { position: [0, 0, 0], rotationEuler: [0, 0, 0], scale: MEDIA_TILE_DEFAULT_SCALE },
        ["Transform"],
        { label: "Align card to origin" },
      );
      return;
    }
    await this.resetChannelsAsync(sceneId, [snapshotId], ["position", "rotation"], "Align to CAD Origins");
  }

  async placeOriginAtModelCenterAsync(sceneId: string, originSnapshotId: string): Promise<void> {
    const position = this.m_geometryHost?.calculateParentBoundsCenterPosition(sceneId, originSnapshotId);
    if (!position) return;
    await updateEntityTransformsWithUndoAsync(
      this.m_project,
      sceneId,
      { writes: [{ snapshotId: originSnapshotId, transform: { position } }] },
      "Place at model center",
    );
  }

  /**
   * Upstream reads `Root`'s RENDERED position, so it first sets the model's
   * explosion factor to zero to read an unexploded one. The stored pose read
   * here is never exploded, so that reset has nothing to protect and is not
   * ported: moving a pivot marker does not collapse the model on the web.
   */
  async placeOriginAtCadOriginAsync(sceneId: string, originSnapshotId: string): Promise<void> {
    const entity = this.m_project.getEntityBySnapshotId(sceneId, originSnapshotId);
    const parent = entity ? findParentSnapshot(entity.snapshots, originSnapshotId) : null;
    // `GetChild(1)` named "Root" (`SelectedGameObjectModel.cs:768`): Origin and
    // Root are siblings under the model root, so Root's local position IS the
    // point upstream converts with `InverseTransformPoint`.
    const contentRoot = parent?.children.find(
      (child) =>
        child.hierachyID === K_CONTENT_ROOT_HIERARCHY_ID && child.name === CONTENT_ROOT_SNAPSHOT_NAME,
    );
    if (!contentRoot) return;
    await updateEntityTransformsWithUndoAsync(
      this.m_project,
      sceneId,
      { writes: [{ snapshotId: originSnapshotId, transform: { position: contentRoot.transform.position } }] },
      "Place at CAD origin",
    );
  }

  /**
   * Each entity's hard-reset value for `channels`, written as one step.
   *
   * A CARD in `snapshotIds` resets its scale only, back to `Vector3.one`
   * (`ProjectModel_Utils.cs:225-234`), in the same step. Its position and
   * rotation have nothing to return to: a card has no imported pose, and
   * Unity's `GetHardResetPosition` / `GetHardResetRotation` answer the CURRENT
   * values for anything that is not an entity (`ProjectModel_Utils.cs:616-636`).
   */
  private async resetChannelsAsync(
    sceneId: string,
    snapshotIds: ReadonlyArray<string>,
    channels: ReadonlyArray<TransformChannel>,
    label: string,
  ): Promise<void> {
    const writes = snapshotIds.flatMap((snapshotId) => {
      const snapshot = this.m_project.getEntitySnapshotById(sceneId, snapshotId);
      if (!snapshot) return [];
      const reset = getResetTransform(snapshot);
      const transform: { -readonly [K in TransformChannel]?: Vec3 } = {};
      for (const channel of channels) transform[channel] = reset[channel];
      return [{ snapshotId, transform }];
    });
    const mediaTiles: MediaTilePoseWrite[] = [];
    if (channels.includes("scale")) {
      for (const tileId of snapshotIds) {
        const tile = this.m_project.getMediaTile(sceneId, tileId);
        if (!tile || tile.isLocked) continue;
        mediaTiles.push({ tileId, pose: { ...mediaTilePose(tile), scale: MEDIA_TILE_DEFAULT_SCALE } });
      }
    }
    if (writes.length === 0 && mediaTiles.length === 0) return;
    await updateEntityTransformsWithUndoAsync(this.m_project, sceneId, { writes, mediaTiles }, label);
  }
}

function getResetTransform(snapshot: EntitySnapshot): EntityTransform {
  return snapshot.resetTransform ?? snapshot.transform;
}

function findParentSnapshot(
  nodes: ReadonlyArray<EntitySnapshot>,
  childId: string,
): EntitySnapshot | null {
  for (const node of nodes) {
    if (node.children.some((child) => child.id === childId)) return node;
    const found = findParentSnapshot(node.children, childId);
    if (found) return found;
  }
  return null;
}
